import React, { useEffect, useState, useMemo } from 'react';
import { Table, TableHeader, TableColumn, TableBody, TableRow, TableCell, Spacer, Button, Chip, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure, Pagination, Spinner, Tabs, Tab, Input, Card, CardBody } from '@nextui-org/react';
import AvailabilityManagement from '../components/AvailabilityManagement';

const AdminPanel = () => {
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const rowsPerPage = 10;

  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewSummary, setOverviewSummary] = useState(null);
  const [overviewBookings, setOverviewBookings] = useState([]);
  const [overviewStartDate, setOverviewStartDate] = useState('');
  const [overviewEndDate, setOverviewEndDate] = useState('');

  const [officeForm, setOfficeForm] = useState({
    startDate: '',
    endDate: '',
    pickupTime: '',
    returnTime: '',
    totalPrice: '',
    singleCount: 0,
    doubleCount: 0,
    firstName: '',
    lastName: '',
    hotel: '',
    phone: '',
    email: '',
    notes: '',
  });
  const [officeSaving, setOfficeSaving] = useState(false);

  useEffect(() => {
    const fetchBookings = async () => {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      try {
        const response = await fetch('https://hotticketsserver-production.up.railway.app/api/bookings', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        if (response.ok) {
          setBookings(data.reverse());
        } else {
          console.error('Failed to fetch bookings:', data.message);
        }
      } catch (error) {
        console.error('Failed to fetch bookings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookings();
  }, []);

  const handleShowData = (booking) => {
    setSelectedBooking(booking);
    onOpen();
  };

  const paginatedBookings = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return bookings.slice(start, start + rowsPerPage);
  }, [page, bookings]);

  const pages = useMemo(() => {
    return Math.ceil(bookings.length / rowsPerPage);
  }, [bookings.length, rowsPerPage]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateShort = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
    });
  };

  const handleCancelBooking = async (bookingId) => {
    const confirmCancel = window.confirm(
      'Are you sure you want to cancel this booking?\n\n' +
      'IMPORTANT: If this booking was paid online, you also need to refund the prepayment manually in the Stripe dashboard.'
    );
    if (!confirmCancel) return;

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`https://hotticketsserver-production.up.railway.app/api/admin/bookings/${bookingId}/cancel`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (response.ok) {
        setBookings(prev =>
          prev.map(b => (b._id === bookingId ? data.booking : b))
        );
        if (selectedBooking && selectedBooking._id === bookingId) {
          setSelectedBooking(data.booking);
        }
      } else {
        console.error('Failed to cancel booking:', data.message);
        alert('Failed to cancel booking');
      }
    } catch (error) {
      console.error('Failed to cancel booking:', error);
      alert('Failed to cancel booking');
    }
  };

  const handleLoadOverview = async () => {
    setOverviewLoading(true);
    const token = localStorage.getItem('token');
    try {
      const params = new URLSearchParams();
      if (overviewStartDate) params.append('startDate', overviewStartDate);
      if (overviewEndDate) params.append('endDate', overviewEndDate);

      const response = await fetch(`https://hotticketsserver-production.up.railway.app/api/admin/bookings-overview?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (response.ok) {
        setOverviewSummary(data.summary);
        setOverviewBookings(data.bookings);
      } else {
        console.error('Failed to load overview:', data.message);
        alert('Failed to load overview');
      }
    } catch (error) {
      console.error('Failed to load overview:', error);
      alert('Failed to load overview');
    } finally {
      setOverviewLoading(false);
    }
  };

  const handleOfficeFormChange = (field, value) => {
    setOfficeForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateOfficeBooking = async () => {
    if (!officeForm.startDate || !officeForm.endDate) {
      alert('Please select start and end dates');
      return;
    }

    if (officeForm.singleCount <= 0 && officeForm.doubleCount <= 0) {
      alert('Please specify at least one scooter');
      return;
    }

    const token = localStorage.getItem('token');
    setOfficeSaving(true);
    try {
      const selectedScooters = [];
      if (officeForm.singleCount > 0) {
        selectedScooters.push({ type: 'single', count: Number(officeForm.singleCount) });
      }
      if (officeForm.doubleCount > 0) {
        selectedScooters.push({ type: 'double', count: Number(officeForm.doubleCount) });
      }

      const body = {
        startDate: officeForm.startDate,
        endDate: officeForm.endDate,
        pickupTime: officeForm.pickupTime,
        returnTime: officeForm.returnTime,
        totalPrice: Number(officeForm.totalPrice) || 0,
        selectedScooters,
        personalDetails: {
          firstName: officeForm.firstName,
          lastName: officeForm.lastName,
          hotel: officeForm.hotel,
          phone: officeForm.phone,
          email: officeForm.email,
        },
        notes: officeForm.notes,
      };

      const response = await fetch('https://hotticketsserver-production.up.railway.app/api/admin/bookings/office', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (response.ok) {
        // Добавляем новую бронь в общий список
        setBookings(prev => [data.booking, ...prev]);
        alert('Office booking created');
        setOfficeForm({
          startDate: '',
          endDate: '',
          pickupTime: '',
          returnTime: '',
          totalPrice: '',
          singleCount: 0,
          doubleCount: 0,
          firstName: '',
          lastName: '',
          hotel: '',
          phone: '',
          email: '',
          notes: '',
        });
      } else {
        console.error('Failed to create office booking:', data.message);
        alert('Failed to create office booking');
      }
    } catch (error) {
      console.error('Failed to create office booking:', error);
      alert('Failed to create office booking');
    } finally {
      setOfficeSaving(false);
    }
  };

  return (
    <div style={{ padding: '16px', maxWidth: '800px', margin: '0 auto' }}>
      <Spacer y={3} />
      <h2 style={{ fontSize: "20px", fontWeight: "600" }}>Admin Panel</h2>
      <Spacer y={5} />

      {/* Tabs для переключения между разделами CRM */}
      <Tabs aria-label="Admin Panel Tabs" size="md">
        <Tab key="bookings" title="Bookings">
          {isLoading ? (
            <Spinner />
          ) : (
            <Table aria-label="Bookings Table" css={{ height: "auto", minWidth: "100%" }}>
              <TableHeader>
                <TableColumn>Booking</TableColumn>
                <TableColumn>Status</TableColumn>
                <TableColumn>Source</TableColumn>
                <TableColumn>Actions</TableColumn>
              </TableHeader>
              <TableBody>
                {paginatedBookings.map((booking) => (
                  <TableRow
                    key={booking._id}
                    style={{
                      borderBottom: '1px solid #E4E4E7',
                    }}
                  >
                    <TableCell>
                      <strong>{booking.personalDetails.firstName} {booking.personalDetails.lastName}</strong>
                      <br />
                      {formatDateShort(booking.startDate)} — {formatDateShort(booking.endDate)}
                      <br />
                      {booking.rentDuration} days
                    </TableCell>
                    <TableCell>
                      <Chip
                        color={booking.status === 'cancelled' ? 'danger' : 'success'}
                        size="sm"
                        variant={booking.status === 'cancelled' ? 'flat' : 'solid'}
                      >
                        {booking.status === 'cancelled' ? 'Cancelled' : 'Active'}
                      </Chip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        color={booking.source === 'office' ? 'warning' : 'primary'}
                        size="sm"
                        variant="flat"
                      >
                        {booking.source === 'office' ? 'Office' : 'Online'}
                      </Chip>
                    </TableCell>
                    <TableCell>
                      <Button
                        auto
                        color="primary"
                        size="sm"
                        variant="flat"
                        onPress={() => handleShowData(booking)}
                      >
                        Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {pages > 1 && (
            <Pagination
              isCompact
              showControls
              showShadow
              color="primary"
              style={{ marginTop: '12px' }}
              page={page}
              total={pages}
              onChange={(page) => setPage(page)}
            />
          )}
          {selectedBooking && (
            <Modal
              isOpen={isOpen}
              onOpenChange={onOpenChange}
              scrollBehavior="inside"
            >
              <ModalContent>
                {(onClose) => (
                  <>
                    <ModalHeader className="flex flex-col gap-1">Booking Details</ModalHeader>
                    <ModalBody>
                      <p><strong>First Name:</strong> {selectedBooking.personalDetails.firstName}</p>
                      <p><strong>Last Name:</strong> {selectedBooking.personalDetails.lastName}</p>
                      <p><strong>Hotel:</strong> {selectedBooking.personalDetails.hotel}</p>
                      <p><strong>Phone:</strong> {selectedBooking.personalDetails.phone}</p>
                      <p><strong>Email:</strong> {selectedBooking.personalDetails.email}</p>
                      <p><strong>Start Date:</strong> {formatDate(selectedBooking.startDate)}</p>
                      <p><strong>End Date:</strong> {formatDate(selectedBooking.endDate)}</p>
                      <p><strong>Pickup Time:</strong> {selectedBooking.pickupTime}</p>
                      <p><strong>Return Time:</strong> {selectedBooking.returnTime}</p>
                      <p><strong>Rent Duration:</strong> {selectedBooking.rentDuration} days</p>
                      <p><strong>Selected Scooters:</strong></p>
                      <ul>
                        {selectedBooking.selectedScooters.map((scooter, index) => (
                          <li key={index}>{scooter.type}: {scooter.count}</li>
                        ))}
                      </ul>
                      <p><strong>Total Price:</strong> {selectedBooking.totalPrice} €</p>
                      <p><strong>Source:</strong> {selectedBooking.source || 'online'}</p>
                      <p><strong>Status:</strong> {selectedBooking.status || 'active'}</p>
                      {selectedBooking.notes && (
                        <p><strong>Notes:</strong> {selectedBooking.notes}</p>
                      )}
                      <p style={{ fontSize: 12, color: '#71717A', marginTop: '8px' }}>
                        If you cancel this booking, it will be excluded from availability calculations.
                        If the prepayment was made online, please remember to process the refund manually in the Stripe dashboard.
                      </p>
                    </ModalBody>
                    <ModalFooter>
                      {selectedBooking.status !== 'cancelled' && (
                        <Button
                          color="danger"
                          variant="flat"
                          onPress={() => handleCancelBooking(selectedBooking._id)}
                        >
                          Cancel booking
                        </Button>
                      )}
                      <Button variant="light" onPress={onClose}>
                        Close
                      </Button>
                    </ModalFooter>
                  </>
                )}
              </ModalContent>
            </Modal>
          )}
        </Tab>

        <Tab key="overview" title="Overview by dates">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row gap-3">
              <Input
                type="date"
                label="Start date"
                value={overviewStartDate}
                onChange={(e) => setOverviewStartDate(e.target.value)}
              />
              <Input
                type="date"
                label="End date"
                value={overviewEndDate}
                onChange={(e) => setOverviewEndDate(e.target.value)}
              />
              <div className="flex items-end">
                <Button color="primary" onPress={handleLoadOverview} isLoading={overviewLoading}>
                  Show
                </Button>
              </div>
            </div>

            {overviewSummary && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Card>
                  <CardBody>
                    <p style={{ fontSize: 12, color: '#71717A' }}>Period</p>
                    <p>{formatDate(overviewSummary.period.start)} — {formatDate(overviewSummary.period.end)}</p>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <p style={{ fontSize: 12, color: '#71717A' }}>Single scooters</p>
                    <p>Total: {overviewSummary.totalSingle}</p>
                    <p>Booked: {overviewSummary.bookedSingle}</p>
                    <p>Available: {overviewSummary.availableSingleScooters}</p>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <p style={{ fontSize: 12, color: '#71717A' }}>Double scooters</p>
                    <p>Total: {overviewSummary.totalDouble}</p>
                    <p>Booked: {overviewSummary.bookedDouble}</p>
                    <p>Available: {overviewSummary.availableDoubleScooters}</p>
                  </CardBody>
                </Card>
              </div>
            )}

            {overviewBookings.length > 0 && (
              <Table aria-label="Overview bookings table">
                <TableHeader>
                  <TableColumn>Customer</TableColumn>
                  <TableColumn>Dates</TableColumn>
                  <TableColumn>Scooters</TableColumn>
                  <TableColumn>Source</TableColumn>
                </TableHeader>
                <TableBody>
                  {overviewBookings.map(booking => (
                    <TableRow key={booking._id}>
                      <TableCell>
                        {booking.personalDetails.firstName} {booking.personalDetails.lastName}
                        <br />
                        {booking.personalDetails.phone}
                      </TableCell>
                      <TableCell>
                        {formatDateShort(booking.startDate)} — {formatDateShort(booking.endDate)}
                        <br />
                        {booking.rentDuration} days
                      </TableCell>
                      <TableCell>
                        {booking.selectedScooters.map((scooter, index) => (
                          <span key={index}>
                            {index > 0 && ', '}
                            {scooter.type}: {scooter.count}
                          </span>
                        ))}
                      </TableCell>
                      <TableCell>
                        <Chip
                          color={booking.source === 'office' ? 'warning' : 'primary'}
                          size="sm"
                          variant="flat"
                        >
                          {booking.source === 'office' ? 'Office' : 'Online'}
                        </Chip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </Tab>

        <Tab key="office" title="Office bookings">
          <div className="flex flex-col gap-4">
            <p style={{ fontSize: 14, color: '#52525B' }}>
              Create and manage bookings made directly in the office. They will be counted in availability checks the same way as online bookings.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                type="date"
                label="Start date"
                value={officeForm.startDate}
                onChange={(e) => handleOfficeFormChange('startDate', e.target.value)}
              />
              <Input
                type="date"
                label="End date"
                value={officeForm.endDate}
                onChange={(e) => handleOfficeFormChange('endDate', e.target.value)}
              />
              <Input
                type="time"
                label="Pickup time"
                value={officeForm.pickupTime}
                onChange={(e) => handleOfficeFormChange('pickupTime', e.target.value)}
              />
              <Input
                type="time"
                label="Return time"
                value={officeForm.returnTime}
                onChange={(e) => handleOfficeFormChange('returnTime', e.target.value)}
              />
              <Input
                type="number"
                label="Single scooters"
                min={0}
                value={String(officeForm.singleCount)}
                onChange={(e) => handleOfficeFormChange('singleCount', Number(e.target.value))}
              />
              <Input
                type="number"
                label="Double scooters"
                min={0}
                value={String(officeForm.doubleCount)}
                onChange={(e) => handleOfficeFormChange('doubleCount', Number(e.target.value))}
              />
              <Input
                type="number"
                label="Total price (€)"
                value={String(officeForm.totalPrice)}
                onChange={(e) => handleOfficeFormChange('totalPrice', e.target.value)}
              />
              <Input
                label="Hotel"
                value={officeForm.hotel}
                onChange={(e) => handleOfficeFormChange('hotel', e.target.value)}
              />
              <Input
                label="First name"
                value={officeForm.firstName}
                onChange={(e) => handleOfficeFormChange('firstName', e.target.value)}
              />
              <Input
                label="Last name"
                value={officeForm.lastName}
                onChange={(e) => handleOfficeFormChange('lastName', e.target.value)}
              />
              <Input
                label="Phone"
                value={officeForm.phone}
                onChange={(e) => handleOfficeFormChange('phone', e.target.value)}
              />
              <Input
                type="email"
                label="Email"
                value={officeForm.email}
                onChange={(e) => handleOfficeFormChange('email', e.target.value)}
              />
              <Input
                label="Notes"
                value={officeForm.notes}
                onChange={(e) => handleOfficeFormChange('notes', e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Button color="primary" onPress={handleCreateOfficeBooking} isLoading={officeSaving}>
                Save office booking
              </Button>
              <p style={{ fontSize: 12, color: '#71717A' }}>
                Note: if the customer paid a deposit in cash or by card in the office, refunds are handled manually outside this panel.
              </p>
            </div>

            <Spacer y={4} />
            <h3 style={{ fontSize: 16, fontWeight: 500 }}>Office bookings list</h3>
            <Table aria-label="Office bookings table">
              <TableHeader>
                <TableColumn>Customer</TableColumn>
                <TableColumn>Dates</TableColumn>
                <TableColumn>Scooters</TableColumn>
                <TableColumn>Status</TableColumn>
              </TableHeader>
              <TableBody>
                {bookings
                  .filter(b => b.source === 'office')
                  .map(booking => (
                    <TableRow key={booking._id}>
                      <TableCell>
                        {booking.personalDetails.firstName} {booking.personalDetails.lastName}
                        <br />
                        {booking.personalDetails.phone}
                      </TableCell>
                      <TableCell>
                        {formatDateShort(booking.startDate)} — {formatDateShort(booking.endDate)}
                      </TableCell>
                      <TableCell>
                        {booking.selectedScooters.map((scooter, index) => (
                          <span key={index}>
                            {index > 0 && ', '}
                            {scooter.type}: {scooter.count}
                          </span>
                        ))}
                      </TableCell>
                      <TableCell>
                        <Chip
                          color={booking.status === 'cancelled' ? 'danger' : 'success'}
                          size="sm"
                          variant={booking.status === 'cancelled' ? 'flat' : 'solid'}
                        >
                          {booking.status === 'cancelled' ? 'Cancelled' : 'Active'}
                        </Chip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </Tab>

        <Tab key="availability" title="Availability">
          {/* Раздел управления доступностью */}
          <AvailabilityManagement />
        </Tab>
      </Tabs>
    </div>
  );
};

export default AdminPanel;
